import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { animate, stagger } from 'motion';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { getHomepageSettings } from '../../../services/contentApi';

const defaultClientImages = [
  '/assets/images/no_image.png',
];

const getImageNameFromPath = (path) => {
  try {
    const parts = path.split('/');
    const file = parts[parts.length - 1];
    const withoutExt = file.replace(/\.[^/.]+$/, '');
    return withoutExt.replace(/\u00A0/g, ' ');
  } catch {
    return '';
  }
};

const HeroSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const imageRef = useRef(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [hero, setHero] = useState(null);
  const [slides, setSlides] = useState(defaultClientImages.map((src) => ({ imageUrl: src })));

  // Load homepage settings (hero and slides)
  useEffect(() => {
    (async () => {
      try {
        const data = await getHomepageSettings();
        if (data?.hero) setHero(data.hero);
        if (Array.isArray(data?.slides) && data.slides.length > 0) {
          setSlides(data.slides);
        }
      } catch {
        // silent fallback to defaults
      }
    })();
  }, []);

  // Autoplay: advance slides automatically
  useEffect(() => {
    if (isLightboxOpen) return; // pause autoplay when lightbox is open
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(interval);
  }, [isLightboxOpen, slides.length]);

  // Animate image on index change
  useEffect(() => {
    if (!imageRef.current) return;
    animate(
      imageRef.current,
      { opacity: [0, 1], transform: ['scale(0.98)', 'scale(1)'] },
      { duration: 0.5, easing: [0.22, 1, 0.36, 1] }
    );
  }, [activeIndex]);

  // Lock body scroll when lightbox is open
  useEffect(() => {
    if (isLightboxOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isLightboxOpen]);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  const handleNext = () => {
    setActiveIndex((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center bg-background animate-fade-in-up">
      {/* Clean hero background (decorative elements removed) */}

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            {/* Logo placed above headline */}
            <div className="flex justify-center lg:justify-start">
              <img
                src="/assets/images/logo.png"
                alt="Nirmitee Associates Logo"
                className="h-64 w-auto object-contain drop-shadow-sm"
              />
            </div>
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-muted rounded-full px-4 py-2 mb-6">
              <Icon name="Shield" size={16} className="text-primary" />
              <span className="text-foreground/90 text-sm font-medium">Pune's #1 Construction Intelligence Platform</span>
            </div>
            {/* Main Headline */}
            <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              {(hero?.title?.line1) || 'Construction clarity in a'}
              <span className="text-secondary block mt-2">{(hero?.title?.line2) || 'complex world'}</span>
            </h1>
            {/* Subheadline */}
            <p className="font-body text-lg sm:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto lg:mx-0">
              {hero?.subtitle || "Transform bureaucratic nightmares into streamlined success. From FSI calculations to regulatory approvals, we're your intelligent construction partner in Pune."}
            </p>
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
              <Button 
                variant="default" 
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                className="text-lg px-8 py-4 bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={() => window.dispatchEvent(new Event('open-consultation-modal'))}
              >
                {hero?.ctaLabel || 'Book Free Consultation'}
              </Button>
              <a href={hero?.ctaHrefSecondary || '#calculator'}>
                <Button 
                  variant="outline" 
                  size="lg"
                  iconName="Calculator"
                  iconPosition="left"
                  className="text-lg px-8 py-4"
                >
                  Try FSI Calculator
                </Button>
              </a>
            </div>
            {/* Trust Indicators */}
            {/* <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-accent" />
                <span className="text-sm">500+ Projects Completed</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-accent" />
                <span className="text-sm">50+ Verified Consultants</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="TrendingUp" size={16} className="text-accent" />
                <span className="text-sm">₹2Cr+ Cost Savings</span>
              </div>
            </div> */}
          </div>

          {/* Right Content - Slideshow */}
          <div className="relative">
            <div className="relative bg-card rounded-2xl construction-shadow">
              <div className="relative flex items-center justify-center min-h-[300px]">
                <Image 
                  ref={imageRef}
                  src={slides[activeIndex]?.imageUrl || defaultClientImages[activeIndex]}
                  alt={getImageNameFromPath(slides[activeIndex]?.imageUrl || '') || `Client Project ${activeIndex + 1}`}
                  className="max-h-[70vh] h-auto w-full md:w-auto object-contain rounded-xl transition-all duration-500 bg-black/5 mx-auto select-none"
                  onClick={() => setIsLightboxOpen(true)}
                  onContextMenu={(e) => e.preventDefault()}
                  draggable={false}
                />
                {/* Navigation Arrows */}
                <button
                  onClick={handlePrev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-foreground/40 text-[#7a5c45] p-2 rounded-full hover:bg-foreground/70 transition-colors z-10"
                  aria-label="Previous image"
                >
                  <Icon name="ChevronLeft" size={24} />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-foreground/40 text-[#7a5c45] p-2 rounded-full hover:bg-foreground/70 transition-colors z-10"
                  aria-label="Next image"
                >
                  <Icon name="ChevronRight" size={24} />
                </button>
                {/* Bottom caption */}
                <div className="absolute bottom-0 left-0 right-0">
                  <div className="m-1 rounded-lg px-3 py-2 bg-[#7a5c45] text-center text-white text-sm">
                    {slides[activeIndex]?.caption || getImageNameFromPath(slides[activeIndex]?.imageUrl || '')}
                  </div>
                </div>
              </div>
              {/* Slide Indicators below image */}
              <div className="flex justify-center mt-4 mb-2">
                <div className="flex space-x-2">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveIndex(idx)}
                      className={`w-3 h-3 rounded-full border-2 ${activeIndex === idx ? 'bg-secondary border-secondary' : 'bg-foreground/20 border-foreground/40'} transition-all`}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>
              </div>

              {/* Fullscreen Lightbox via portal to escape stacking contexts */}
              {isLightboxOpen && createPortal(
                (
                  <div
                    className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
                    onClick={() => setIsLightboxOpen(false)}
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <button
                      aria-label="Close"
                      className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
                      onClick={() => setIsLightboxOpen(false)}
                    >
                      <Icon name="X" size={20} />
                    </button>
                    <div className="relative max-h-[95vh] max-w-[95vw] w-full flex items-center justify-center">
                      <img
                        src={slides[activeIndex]?.imageUrl || defaultClientImages[activeIndex]}
                        alt={getImageNameFromPath(slides[activeIndex]?.imageUrl || '') || 'Project image'}
                        className="max-h-[95vh] max-w-[95vw] object-contain select-none"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}
                      />
                      {/* Caption inside lightbox */}
                      <div className="absolute bottom-0 left-0 right-0">
                        <div className="m-2 rounded-lg px-3 py-2 bg-[#7a5c45] text-center text-white text-sm">
                          {slides[activeIndex]?.caption || getImageNameFromPath(slides[activeIndex]?.imageUrl || '')}
                        </div>
                      </div>
                    </div>
                    {/* Watermark overlay deterrent */}
                    <div className="pointer-events-none select-none absolute inset-0 flex items-end justify-center p-6">
                      <div className="text-white/30 text-xs">Nirmitee Associates</div>
                    </div>
                  </div>
                ),
                document.body
              )}
              {/* Floating Stats */}
              {/* <div className="absolute -top-4 -right-4 bg-accent text-white rounded-lg p-4 construction-shadow">
                <div className="text-center">
                  <div className="text-2xl font-bold">98%</div>
                  <div className="text-xs opacity-90">Success Rate</div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-secondary text-white rounded-lg p-4 construction-shadow">
                <div className="text-center">
                  <div className="text-2xl font-bold">15</div>
                  <div className="text-xs opacity-90">Days Avg</div>
                </div>
              </div> */}
            </div>
            {/* Decorative elements removed for a clean look */}
          </div>
        </div>
      </div>

      {/* Scroll indicator removed */}
    </section>
  );
};

export default HeroSection;